function normalizeOrigin(origin: string): string {
    return origin.replace(/\/+$/, "");
}

/** Registered in Entra today; tenant slug hosts use this until wildcard URIs are added. */
export const CANONICAL_STAGING_OAUTH_CALLBACK =
    "https://app.staging.savira.io/auth/callback";

export const POST_AUTH_ORIGIN_KEY = "savira_post_auth_origin";
const POST_AUTH_ORIGIN_COOKIE = "savira_post_auth_origin";

/** MSAL OAuth redirect — must match a registered SPA redirect URI in Entra. */
export function getEntraOAuthRedirectUri(origin: string): string {
    const configured =
        process.env.NEXT_PUBLIC_ENTRA_REDIRECT_URI?.trim() ||
        process.env.ENTRA_REDIRECT_URI?.trim();
    if (configured) return configured;

    try {
        const host = new URL(origin).hostname.toLowerCase();
        if (host.endsWith(".staging.savira.io") && host !== "app.staging.savira.io") {
            return CANONICAL_STAGING_OAUTH_CALLBACK;
        }
    } catch {
        /* fall through */
    }

    return `${normalizeOrigin(origin)}/auth/callback`;
}

/** Origin where MSAL must start and finish the OAuth redirect (PKCE state is per-origin). */
export function getEntraOAuthCallbackOrigin(origin: string): string {
    return new URL(getEntraOAuthRedirectUri(origin)).origin;
}

/** Tenant slug hosts delegate interactive OAuth to the canonical callback host. */
export function shouldDelegateOAuthToCanonicalHost(origin: string): boolean {
    return normalizeOrigin(origin) !== normalizeOrigin(getEntraOAuthCallbackOrigin(origin));
}

export function buildCanonicalOAuthSignInUrl(origin: string): string {
    const params = new URLSearchParams({
        entraRedirect: "1",
        returnOrigin: normalizeOrigin(origin),
    });
    return `${getEntraOAuthCallbackOrigin(origin)}/auth/signin?${params.toString()}`;
}

function sharedCookieDomain(origin: string): string | null {
    try {
        const host = new URL(origin).hostname.toLowerCase();
        if (host.endsWith(".staging.savira.io") || host === "app.staging.savira.io") {
            return ".staging.savira.io";
        }
        if (host.endsWith(".savira.io")) {
            return ".savira.io";
        }
    } catch {
        /* ignore */
    }
    return null;
}

function setSharedCookie(name: string, value: string, origin: string): void {
    const domain = sharedCookieDomain(origin);
    if (!domain || typeof document === "undefined") return;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; domain=${domain}; SameSite=Lax; max-age=3600; Secure`;
}

function readSharedCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function clearSharedCookie(name: string, origin: string): void {
    const domain = sharedCookieDomain(origin);
    if (!domain || typeof document === "undefined") return;
    document.cookie = `${name}=; path=/; domain=${domain}; max-age=0; Secure`;
}

export function rememberPostAuthOrigin(origin: string): void {
    if (typeof window === "undefined") return;
    const normalized = normalizeOrigin(origin);
    sessionStorage.setItem(POST_AUTH_ORIGIN_KEY, normalized);
    setSharedCookie(POST_AUTH_ORIGIN_COOKIE, normalized, origin);
}

export function consumePostAuthOrigin(): string | null {
    if (typeof window === "undefined") return null;
    const fromSession = sessionStorage.getItem(POST_AUTH_ORIGIN_KEY);
    if (fromSession) sessionStorage.removeItem(POST_AUTH_ORIGIN_KEY);
    const fromCookie = readSharedCookie(POST_AUTH_ORIGIN_COOKIE);
    if (fromCookie) clearSharedCookie(POST_AUTH_ORIGIN_COOKIE, window.location.origin);
    return fromSession || fromCookie;
}

function hostnameForSlug(slug: string, environment: string): string {
    return environment === "production"
        ? `${slug}.savira.io`
        : `${slug}.staging.savira.io`;
}

/**
 * Where Microsoft sends a guest after they accept the invitation email.
 * Must NOT be /auth/callback — that route expects an MSAL OAuth response.
 * Land on sign-in instead; the UI auto-starts interactive Microsoft login.
 */
export function buildEntraInviteRedirectUrl(params: {
    origin?: string;
    slug?: string;
    environment?: string;
}): string {
    const configured =
        process.env.ENTRA_INVITE_REDIRECT_URL?.trim() ||
        process.env.NEXT_PUBLIC_ENTRA_INVITE_REDIRECT_URL?.trim();
    if (configured) return configured;

    if (params.slug && params.environment) {
        const host = hostnameForSlug(params.slug, params.environment);
        return `https://${host}/auth/signin?invited=1`;
    }

    const origin = params.origin?.trim();
    if (!origin) {
        throw new Error("buildEntraInviteRedirectUrl requires origin or slug+environment");
    }
    return `${normalizeOrigin(origin)}/auth/signin?invited=1`;
}

/** True when the current URL carries an OAuth redirect response (code or error). */
export function urlHasOAuthRedirectResponse(search: string, hash: string): boolean {
    const combined = `${search}${hash}`.toLowerCase();
    return (
        combined.includes("code=") ||
        combined.includes("id_token=") ||
        combined.includes("error=")
    );
}
